import { useNavigate, useLocation } from "react-router-dom";
import { imoji, noStudent, ptmImoji } from "../asset";
import CustomButton from "./CustomButton";
import { plus } from "../asset";
import { JSX } from "react";

interface EmptyTableProps {
  buttonColor?: string;
  buttonIcon?: string;
  buttonTextColor?: string;
  buttonBorder?: string;
  customButton?: JSX.Element;
}

export default function EmptyTable({
  buttonColor = "bg-primary-500",
  buttonIcon,
  buttonTextColor = "text-white",
  buttonBorder,
  customButton,
}: EmptyTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get("tab")?.toLowerCase();

  const lowerPath = path.toLowerCase();

  // Different text + image based on the current page
  let heading = "Nothing here yet..!";
  let subText =
    "No records have been added so far. Once data is available, it will appear here.";
  let buttonText = "";
  let image = "";
  let showButton = false;

  if (lowerPath.includes("student-profile")) {
    heading = "Nothing here yet..!";
    subText = "No records found for this student.";
    image = "";
    showButton = false;
  } else if (lowerPath.includes("students")) {
    heading = "Oops! No students here yet.";
    subText =
      "Add your first student in just a few quick steps and get started right away!";
    buttonText = "Add Student";
    image = noStudent;
    showButton = true;
  } else if (lowerPath === "/staff") {
    heading = "No staff members found.";
    subText =
      "Add your first staff member to manage academic activities efficiently.";
    buttonText = "Add Staff";
    image = imoji;
    showButton = true;
  } else if (lowerPath.startsWith("/classes") && lowerPath === "/classes") {
    heading = "Hmm… no entries made yet!";
    subText =
      "Start by adding classes for each academic cycle to organize your students easily.";
    buttonText = "Add Class";
    image = imoji;
    showButton = true;
  } else if (lowerPath.includes("student-profile") && tab === "others") {
    heading = "";
    subText = "No group or house assigned for this student !!";
    image = "";
    buttonText = "Add to Group/House";
    showButton = true;
    buttonColor = "bg-success-600";
    buttonIcon = plus;
  } else if (lowerPath.includes("ptm-list")) {
    heading = "No PTM found !!";
    subText = "Schedule a PTM to connect with parents in just few steps";
    image = ptmImoji;
    buttonText = "Schedule New PTM";
    showButton = true;
    buttonColor = "bg-primary-500";
  }

  const handleAddClick = () => {
    if (lowerPath.includes("students")) {
      navigate("/all-students/add-student");
    } else if (lowerPath.includes("staff")) {
      navigate("/staff/add-staff");
    } else if (lowerPath.includes("student-profile") && tab === "others") {
      navigate("/groups-houses/add-group");
    } else {
      const encoded = btoa("addClasses");
      navigate(`?popup=${encoded}`, { replace: false });
    }
  };

  return (
    <div
      className={`flex flex-col items-center gap-2 pb-5 ${
        !image ? "pt-14" : "pt-5"
      }`}
    >
      {image && (
        <div>
          <img src={image} alt={heading} />
        </div>
      )}
      <div className="text-center">
        <h3 className="text-2xl font-semibold text-neutral-400 mb-2">
          {heading}
        </h3>
        <p className="text-neutral-300 text-base font-semibold max-w-xl">
          {subText}
        </p>
      </div>
      <div className="mt-4">
        {showButton && (
          <div className="">
            {customButton || (
              <CustomButton
                text={buttonText}
                color={buttonColor}
                textColor={buttonTextColor}
                icon={buttonIcon}
                border={buttonBorder}
                onClick={handleAddClick}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
